import { Query } from '../../query';
import {
  Adapter,
  AfterCommitStandaloneHook,
  SqlSessionState,
} from '../../../adapters/adapter';
import { emptyObject } from '../../../utils';
import { OrchidOrmError } from '../../errors';
import { PickQueryQAndInternal } from '../../pick-query-types';
import { _clone } from '../clone/clone';
import { AsyncState, processStorageOptions } from '../storage/storage';
import { QuerySchema } from '../schema/schema';

export interface AsyncTransactionState extends AsyncState {
  transactionAdapter: Adapter;
  transactionId: number;
}

export type IsolationLevel =
  | 'SERIALIZABLE'
  | 'REPEATABLE READ'
  | 'READ COMMITTED'
  | 'READ UNCOMMITTED';

export interface TransactionOptions extends SqlSessionState {
  /**
   * Enable or disable logging for all queries in the transaction.
   */
  log?: boolean;
  /**
   * Default schema to use for queries in the transaction.
   */
  schema?: QuerySchema;
  /**
   * Postgres isolation level for the top-level transaction.
   */
  level?: IsolationLevel;
  /**
   * Whether the top-level transaction should be read-only.
   */
  readOnly?: boolean;
  /**
   * Whether the top-level transaction should be deferrable.
   */
  deferrable?: boolean;
  /**
   * Postgres role to apply for the transaction callback.
   *
   * Nested transactions can temporarily replace the parent transaction role.
   */
  role?: SqlSessionState['role'];
  /**
   * Postgres custom settings to apply for the transaction callback.
   *
   * Nested transaction settings are shallow-merged over the parent transaction
   * settings while the nested callback runs.
   */
  setConfig?: SqlSessionState['setConfig'];
}

export interface AfterCommitErrorFulfilledResult extends PromiseFulfilledResult<unknown> {
  name?: string;
}

export interface AfterCommitErrorRejectedResult extends PromiseRejectedResult {
  name?: string;
}

export type AfterCommitErrorResult =
  | AfterCommitErrorFulfilledResult
  | AfterCommitErrorRejectedResult;

/**
 * `AfterCommitError` is thrown when one of after commit hooks throws.
 *
 * ```ts
 * interface AfterCommitError extends OrchidOrmError {
 *   // the result of transaction functions
 *   result: unknown;
 *
 *   // Promise.allSettled result + optional function names
 *   hookResults: (
 *     | {
 *         status: 'fulfilled';
 *         value: unknown;
 *         name?: string;
 *       }
 *     | {
 *         status: 'rejected';
 *         reason: any; // the error object thrown by a hook
 *         name?: string;
 *       }
 *   )[];
 * }
 * ```
 *
 * Use `function name() {}` function syntax for hooks to give them names,
 * so later they can be identified when handling after commit errors.
 *
 * ```ts
 * class SomeTable extends BaseTable {
 *   readonly table = 'someTable';
 *   columns = this.setColumns((t) => ({
 *     ...someColumns,
 *   }));
 *
 *   init(orm: typeof db) {
 *     // anonymous funciton - has no name
 *     this.afterCreateCommit([], async () => {
 *       // ...
 *     });
 *
 *     // named function
 *     this.afterCreateCommit([], function myHook() => {
 *       // ...
 *     });
 *   }
 * }
 * ```
 */
export class AfterCommitError extends OrchidOrmError {
  constructor(
    public result: unknown,
    public hookResults: AfterCommitErrorResult[],
  ) {
    super('After commit hooks have failed');
  }
}

export type AfterCommitErrorHandler = (
  error: AfterCommitError,
) => void | Promise<void>;

export const _runAfterCommitHooks = async (
  result: unknown,
  promises: unknown[],
  getHookNames: () => string[],
  catchAfterCommitErrors: AfterCommitErrorHandler[] | undefined,
) => {
  const hookResults = await Promise.allSettled(promises);
  if (hookResults.some((result) => result.status === 'rejected')) {
    const hookNames = getHookNames();

    for (const [i, r] of hookResults.entries()) {
      (r as AfterCommitErrorResult).name = hookNames[i];
    }

    const err = new AfterCommitError(result, hookResults);
    if (!catchAfterCommitErrors) throw err;

    for (const fn of catchAfterCommitErrors) {
      try {
        fn(err);
      } catch {}
    }
  }
};

/**
 * Check if inside transaction started by user (not test transaction).
 */
export const isInUserTransaction = (
  trx: AsyncState | undefined,
): trx is AsyncState =>
  trx?.transactionId !== undefined &&
  // when inside test transactions, compare transaction counts to ensure there is a user transaction.
  (!trx.testTransactionCount || trx.transactionId >= trx.testTransactionCount);

export class QueryTransaction {
  /**
   * In Orchid ORM the method is `$transaction`, when using `pqb` on its own it is `transaction`.
   *
   * `COMMIT` happens automatically after the callback was successfully resolved, and `ROLLBACK` is done automatically if the callback fails.
   *
   * Let's consider the case of transferring money from one user to another:
   *
   * ```ts
   * export const transferMoney = async (
   *   fromId: number,
   *   toId: number,
   *   amount: number,
   * ) => {
   *   try {
   *     // db.$transaction returns data that is returned from the callback
   *     // result here is senderRemainder
   *     const result = await db.$transaction(async () => {
   *       const sender = await db.user.find(fromId);
   *       const senderRemainder = sender.balance - amount;
   *       if (senderRemainder < 0) {
   *         throw new Error('Sender does not have enough money');
   *       }
   *
   *       await db.user.find(fromId).decrement({
   *         balance: amount,
   *       });
   *       await db.user.find(toId).increment({
   *         balance: amount,
   *       });
   *
   *       return senderRemainder;
   *     });
   *   } catch (error) {
   *     // handle transaction error
   *   }
   * };
   * ```
   *
   * It performs 3 queries in a single transaction: load sender record, decrement sender's balance, increment receiver's balance.
   *
   * If sender or receiver record doesn't exist, it will throw `NotFound` error, and there is an error thrown when sender's balance is too low.
   * In such case, the transaction will be rolled back and no changes will be applied to the database.
   *
   * Internally, ORM relies on [AsyncLocalStorage](https://nodejs.org/api/async_context.html#class-asynclocalstorage) feature of node.js,
   * it allows passing the transaction object implicitly. So that any query that is done inside of callback, will run inside a transaction.
   *
   * ## nested transactions
   *
   * Transactions can be nested one in another.
   * The top level transaction is the real one,
   * and the nested ones are emulated with [savepoint](https://www.postgresql.org/docs/current/sql-savepoint.html) instead of `BEGIN`
   * and [release savepoint](https://www.postgresql.org/docs/current/sql-release-savepoint.html) instead of `COMMIT`.
   *
   * Use [ensureTransaction](#ensuretransaction) to run all queries in a single transaction.
   *
   * ```ts
   * const result = await db.$transaction(async () => {
   *   await db.table.create(...one);
   *
   *   const result = await db.$transaction(async () => {
   *     await db.table.create(...two);
   *     return 123;
   *   });
   *
   *   await db.table.create(...three);
   *
   *   return result;
   * });
   *
   * // result is returned from the inner transaction
   * result === 123;
   * ```
   *
   * If the inner transaction throws an error, and it is caught by `try/catch` of outer transaction,
   * it performs [rollback to savepoint](https://www.postgresql.org/docs/current/sql-rollback-to.html)
   * and the outer transaction can continue:
   *
   * ```ts
   * class CustomError extends Error {}
   *
   * await db.$transaction(async () => {
   *   try {
   *     await db.$transaction(async () => {
   *       throw new CustomError();
   *     });
   *   } catch (err) {
   *     if (err instanceof CustomError) {
   *       // ignore this error
   *       return;
   *     }
   *     throw err;
   *   }
   *
   *   // this transaction can continue
   *   await db.table.create(...data);
   * });
   * ```
   *
   * If the error in the inner transaction is not caught, all nested transactions are rolled back and aborted.
   *
   * ## SQL session context in transactions
   *
   * Pass `role` and `setConfig` in transaction options to apply Postgres SQL session context for the whole transaction:
   *
   * ```ts
   * await db.$transaction(
   *   {
   *     role: 'app_user',
   *     setConfig: {
   *       'app.tenant_id': tenantId,
   *       'app.user_id': userId,
   *     },
   *   },
   *   async () => {
   *     const project = await db.project.find(projectId);
   *
   *     await db.project.find(projectId).update({ lastViewedAt: new Date() });
   *
   *     return project;
   *   },
   * );
   * ```
   *
   * This is different from `$withOptions({ role, setConfig }, cb)`.
   * `$withOptions` is query-scoped and reconciles SQL session state around each query.
   * Transaction options are applied once for the transaction and are useful for request-scoped RLS work that is already transaction-bound.
   *
   * Nested transactions can temporarily override the parent transaction role and config.
   * When the nested transaction finishes, Orchid restores the parent transaction context before the outer callback continues.
   */
  transaction<Result>(
    this: PickQueryQAndInternal,
    cb: () => Promise<Result>,
  ): Promise<Result>;
  transaction<Result>(
    this: PickQueryQAndInternal,
    options: IsolationLevel | TransactionOptions,
    cb: () => Promise<Result>,
  ): Promise<Result>;
  async transaction<Result>(
    this: PickQueryQAndInternal,
    cbOrOptions: IsolationLevel | TransactionOptions | (() => Promise<Result>),
    cb?: () => Promise<Result>,
  ): Promise<Result> {
    let options: TransactionOptions;
    let fn: () => Promise<Result>;
    if (typeof cbOrOptions === 'function') {
      options = emptyObject as TransactionOptions;
      fn = cbOrOptions;
    } else {
      options =
        typeof cbOrOptions === 'object' ? cbOrOptions : { level: cbOrOptions };
      fn = cb as () => Promise<Result>;
    }

    let opts = processStorageOptions(this, undefined, options);

    return this.q.adapter.transaction(this.internal.asyncStorage, opts, fn);
  }

  /**
   * Use the `$ensureTransaction` when you want to ensure the sequence of queries is running in a transaction, but there is no need for Postgres [savepoints](https://www.postgresql.org/docs/current/sql-savepoint.html).
   *
   * ```ts
   * async function updateUserBalance(userId: string, amount: number) {
   *   await db.$ensureTransaction(async () => {
   *     await db.transfer.create({ userId, amount })
   *     await db.user.find(userId).increment({ balance: amount })
   *   })
   * }
   *
   * async function saveDeposit(userId: string, deposit: { ... }) {
   *   await db.$ensureTransaction(async () => {
   *     await db.deposit.create(deposit)
   *     // transaction in updateUserBalance won't be started
   *     await updateUserBalance(userId, deposit.amount)
   *   })
   * }
   * ```
   */
  ensureTransaction<Result>(
    this: PickQueryQAndInternal,
    cb: () => Promise<Result>,
  ): Promise<Result> {
    const trx = this.internal.asyncStorage.getStore();
    if (trx) return cb();

    return (
      QueryTransaction.prototype.transaction as (cb: unknown) => Promise<Result>
    ).call(this, cb) as Promise<Result>;
  }

  isInTransaction(): boolean {
    const trx = (this as unknown as Query).internal.asyncStorage.getStore();

    return isInUserTransaction(trx);
  }

  /**
   * Schedules a hook to run after the outermost transaction commits:
   *
   * ```ts
   * await db.$transaction(async () => {
   *   await db.table.create(data)
   *   await db.table.where({ ...conditions }).update({ key: 'value' })
   *
   *   db.$afterCommit(() => { // can be sync or async
   *     console.log('after commit')
   *   })
   * })
   * ```
   *
   * If used outside the transaction, the hook will be executed almost immediately, on the next microtask:
   *
   * ```ts
   * db.$afterCommit(async () => { // can be sync or async
   *   console.log('after commit')
   * })
   * ```
   *
   * If the callback has no `try/catch` and throws an error,
   * this will cause `uncaughtException` if the callback is sync and `unhandledRejection` if it is async.
   */
  afterCommit(this: Query, hook: AfterCommitStandaloneHook): void {
    const trx = this.internal.asyncStorage.getStore();
    if (isInUserTransaction(trx)) {
      (trx.afterCommit ??= []).push(hook);
    } else {
      queueMicrotask(hook);
    }
  }

  /**
   * Whenever a query in a transaction fails, the transaction is transitioned to a failed state, no further queries are possible.
   *
   * Use [catchUniqueError](/guide/error-handling.html#catchuniqueerror) to handle uniqueness errors,
   * the following examples do not use it to illustrate error handling.
   *
   * ```ts
   * // This transaction is going to fail
   * db.$transaction(async () => {
   *   try {
   *     // imagine it fails because the username is already taken
   *     await db.user.insert({ username: 'taken' });
   *   } catch (err) {
   *     // even though the query is wrapped in a try-catch, the transaction fails anyway
   *   }
   * });
   * ```
   *
   * You can use `catch` method on a query instead of `try-catch` to surpass the problem.
   * The following transaction won't fail:
   *
   * ```ts
   * // Transaction succeeds
   * db.$transaction(async () => {
   *   const result = await db.user.insert({ username: 'taken' }).catch(() => 'failed');
   *
   *   if (result === 'failed') {
   *     await db.user.insert({ username: 'another' });
   *   }
   * });
   * ```
   *
   * This is because when using `catch` method, `OrchidORM` will wrap the query with a savepoint.
   *
   * Alternatively, you can use the `recoverable()` method:
   *
   * ```ts
   * // Transaction succeeds
   * db.$transaction(async () => {
   *   try {
   *     await db.user.insert({ username: 'taken' }).recoverable();
   *   } catch (err) {
   *     await db.user.insert({ username: 'another' });
   *   }
   * });
   * ```
   */
  recoverable<T>(this: T): T {
    const q = _clone(this);
    q.q.catch = true;
    return q as T;
  }
}
